import { useState } from 'react';
import { Mic, Square } from 'lucide-react';
import { Button } from '@/components/ui';
import { PreHearingModal } from '@/components/PreHearingModal';
import { useTranscriptionContext } from '@/contexts/TranscriptionContext';
import { useSession } from '@/contexts/SessionContext';
import { isPreHearingAcked, markPreHearingAcked } from '@/lib/prehearing';

// Two-state listening control per 03-ui-design-language.md "Listening button".
// Idle: emerald, microphone icon, "Start listening".
// Active: red, pulsing white dot, "Listening". Stop on click.
//
// P5.4: the first Start Listening of a session opens the lawful-use checklist
// (PreHearingModal); listening only begins once the operator acknowledges it.
export function ListeningButton() {
  const { isListening, supported, start, stop } = useTranscriptionContext();
  const { sessionId } = useSession();
  const [showChecklist, setShowChecklist] = useState(false);

  const handleStartRequest = (): void => {
    if (isPreHearingAcked(sessionId)) {
      start();
    } else {
      setShowChecklist(true);
    }
  };

  const handleConfirm = (): void => {
    markPreHearingAcked(sessionId);
    setShowChecklist(false);
    start();
  };

  if (!supported) {
    return (
      <Button
        variant="secondary"
        size="sm"
        leftIcon={<Mic className="h-4 w-4" />}
        aria-label="Browser does not support speech recognition"
        disabled
      >
        Mic unsupported
      </Button>
    );
  }

  if (isListening) {
    return (
      <Button
        variant="listeningActive"
        size="sm"
        leftIcon={<PulsingDot />}
        rightSlot={<Square className="h-3 w-3 fill-white" aria-hidden />}
        aria-label="Stop listening"
        onClick={stop}
      >
        Listening
      </Button>
    );
  }

  return (
    <>
      <Button
        variant="listeningIdle"
        size="sm"
        leftIcon={<Mic className="h-4 w-4" />}
        aria-label="Start listening"
        onClick={handleStartRequest}
      >
        Start listening
      </Button>
      <PreHearingModal
        open={showChecklist}
        onConfirm={handleConfirm}
        onCancel={() => setShowChecklist(false)}
      />
    </>
  );
}

function PulsingDot() {
  return (
    <span className="relative inline-flex h-2 w-2">
      <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-white opacity-75" />
      <span className="relative inline-flex h-2 w-2 rounded-full bg-white" />
    </span>
  );
}
